import { pointAtDistance } from './alongPath.js';
import {
  FERRY_WAKE_MS,
  FERRY_WAKE_SEGMENTS,
  FERRY_WAKE_ALPHA_NEAR,
  FERRY_WAKE_ALPHA_FAR,
} from './ferryMotion.js';

export interface WakeSegment {
  source: [number, number];
  target: [number, number];
  alpha: number;
  width: number;
}

/**
 * Fading wake along the ROUTE PATH behind an underway boat at `boatDist` (m
 * along `path`): covers the boat's last FERRY_WAKE_MS of travel at `speedMps`,
 * clamped at the path start, as contiguous segments whose alpha/width ramp up
 * toward the boat. The path-window variant of the glide-chord wake — it follows
 * the route curve instead of cutting across it.
 */
export function wakeAlongPath(
  path: [number, number][],
  cum: number[],
  boatDist: number,
  speedMps: number,
): WakeSegment[] {
  const lengthM = Math.max(0, speedMps) * (FERRY_WAKE_MS / 1000);
  const tail = Math.max(cum[0] ?? 0, boatDist - lengthM);
  if (boatDist - tail < 1) return [];

  const span = boatDist - tail;
  return Array.from({ length: FERRY_WAKE_SEGMENTS }, (_, k) => {
    const d0 = tail + (span * k) / FERRY_WAKE_SEGMENTS;
    const d1 = tail + (span * (k + 1)) / FERRY_WAKE_SEGMENTS;
    const t = (k + 1) / FERRY_WAKE_SEGMENTS; // k+1 closer to the boat → stronger, wider
    return {
      source: pointAtDistance(path, cum, d0),
      target: pointAtDistance(path, cum, d1),
      alpha: Math.round(FERRY_WAKE_ALPHA_FAR + (FERRY_WAKE_ALPHA_NEAR - FERRY_WAKE_ALPHA_FAR) * t),
      width: 1 + 2 * t,
    };
  });
}
